
import {
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  inject,
  OnInit,
  signal,
  ViewChild,
  computed,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { AiKnowledgeService } from '../../services/ai-knowledge.service';
import { FileDownloadService } from '../../services/file-download.service';
import { Environment, EnvironmentService } from '@galaxy/core';

import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApplicationSelectorComponent } from '../../application-selector/application-selector.component';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { KnowledgeSourceWithSuggestedFlag, isTrainingFinished } from '../../ai-knowledge-util';

import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { WebChatWidgetService } from '../../services/web-chat-widget.service';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import {
  KnowledgeSourceConfig,
  KnowledgeSourceConfigFileConfig,
  KnowledgeSourceKey,
  TrainingProgress,
  KnowledgeSourceTraining,
  KnowledgeSourceTrainingErrorErrorCode,
  KnowledgeSourceConfigFileConfigFile,
} from '@vendasta/embeddings';
import { KnowledgeCreatedEvent, StepChangeEvent } from '../common/interfaces';
import {
  FileInfo,
  FileUploadError,
  FileUploadStatus,
  GalaxyUploaderModule,
  GalaxyUploaderService,
  GALAXY_UPLOADER_SERVICE_TOKEN,
} from '@vendasta/galaxy/uploader';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FileTypeIconService } from '../../services/file-type-icon.service';
import { TrainingProgressComponent } from '../../shared/training-progress/training-progress.component';
import {
  TrainingProgressConfig,
  shouldShowTrainingProgress,
} from '../../shared/training-progress/training-progress.utils';
import { defer, from, retry, map, take } from 'rxjs';
import mime from 'mime';

interface FileUploadState {
  file: FileInfo | null;
  jwtToken: string | null;
  isUploading: boolean;
  hasBeenSaved: boolean;
  hasExistingFile: boolean;
}

interface Data {
  knowledgeSourceKey?: KnowledgeSourceKey;
  excludeAddToAIStep?: boolean;
  currentAppId?: string;
}

@Component({
  selector: 'ai-knowledge-file-upload',
  imports: [
    TranslateModule,
    AiKnowledgeI18nModule,
    MatInputModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    GalaxyButtonGroupModule,
    GalaxySnackbarModule,
    MatStepperModule,
    MatIconModule,
    MatTooltipModule,
    ApplicationSelectorComponent,
    MatButton,
    MatDialogActions,
    MatDialogContent,
    GalaxyUploaderModule,
    TrainingProgressComponent,
    GalaxyAlertModule
],
  providers: [
    AiKnowledgeService,
    WebChatWidgetService,
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    {
      provide: GALAXY_UPLOADER_SERVICE_TOKEN,
      useClass: GalaxyUploaderService,
    },
  ],
  templateUrl: './file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, KnowledgeCreatedEvent, StepChangeEvent {
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('applicationSelector') applicationSelector!: ApplicationSelectorComponent;

  private readonly snackbarService = inject(SnackbarService);
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly webChatWidgetService = inject(WebChatWidgetService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly translateService = inject(TranslateService);

  private readonly environmentService = inject(EnvironmentService);
  private readonly fileTypeIconService = inject(FileTypeIconService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly otherAIDisclaimer = signal('');
  readonly isLoading = signal(true);

  // a string corresponding to the current step should be emitted whenever a step changes
  // this will be used to update the address to the current step, so that it can be linked to directly
  stepChange = new EventEmitter<string>();
  excludeAddToAIStep = false;
  // a list of strings corresponding to the step indexes for a stepper
  steps = signal<string[]>(['']);
  // the current step
  selectedStep = signal(0);

  form: FormGroup;
  knowledgeSource = signal<KnowledgeSourceWithSuggestedFlag | null>(null);
  knowledgeCreated = new EventEmitter<string>();

  delayProgressScreenClosure = signal(false);
  submitDisabled = signal(false);
  shouldShowDone = signal(false);
  isDismissingError = signal(false);

  get trainingProgressConfig(): TrainingProgressConfig {
    return {
      trainingInfo: this.knowledgeSource()?.training ?? null,
      delayProgressScreenClosure: this.delayProgressScreenClosure(),
    };
  }

  shouldShowTrainingProgress(): boolean {
    return shouldShowTrainingProgress(this.trainingProgressConfig, this.shouldShowDone());
  }

  // File upload related properties
  uploadUrl!: string;
  private readonly uploadHosts = new Map<Environment, string>([
    [Environment.DEMO, 'https://embeddings-demo.apigateway.co'],
    [Environment.PROD, 'https://embeddings-prod.apigateway.co'],
    [Environment.LOCAL, 'https://embeddings-demo.apigateway.co'], // Use demo for local development
    [Environment.TEST, 'https://embeddings-demo.apigateway.co'], // Use demo for test
  ]);
  maxFiles = 1;
  maxFileSize = 50 * 1024 * 1024; // 50MB
  acceptedFileTypes =
    '.csv,.docx,.html,.htm,.jpg,.jpeg,.png,.pdf,.txt,.text,.md,.markdown,.json,.jsonl,.pptx,.xlsx,.xls';

  // Consolidated file state
  fileState = signal<FileUploadState>({
    file: null,
    jwtToken: null,
    isUploading: false,
    hasBeenSaved: false,
    hasExistingFile: false,
  });

  // Computed properties for template convenience and derived state
  readonly uploadedFile = computed(() => this.fileState().file);
  readonly hasExistingFileOnly = computed(
    () => !!this.knowledgeSource()?.config?.fileConfig?.fileUrl && !this.fileState().file,
  );
  readonly hasTrainingError = computed(
    () =>
      this.knowledgeSource()?.training?.progress === TrainingProgress.TRAINING_ERRORED &&
      !this.delayProgressScreenClosure(),
  );
  readonly trainingErrorMessageKey = computed(() => {
    const errorCode = this.knowledgeSource()?.training?.error?.code;
    if (!errorCode) {
      return null;
    }

    switch (errorCode) {
      case KnowledgeSourceTrainingErrorErrorCode.ERROR_CODE_LARGE_FILE_CONTENT:
        return 'EDIT_KNOWLEDGE.TRAINING.ERROR_LARGE_FILE_CONTENT';
      case KnowledgeSourceTrainingErrorErrorCode.ERROR_CODE_UNPROCESSIBLE_FILE_CONTENT:
        return 'EDIT_KNOWLEDGE.TRAINING.ERROR_UNPROCESSIBLE_FILE_CONTENT';
      default:
        return 'EDIT_KNOWLEDGE.TRAINING.ERROR_UNKNOWN';
    }
  });
  readonly canDismissError = computed(() => !!this.knowledgeSource()?.config?.fileConfig?.fileUrl);

  // Current file display information computed signal
  readonly currentFileInfo = computed(() => {
    const fileConfig = this.knowledgeSource()?.config?.fileConfig;
    if (!fileConfig) {
      return null;
    }

    const trainingProgress = this.knowledgeSource()?.training?.progress;

    let fileUrl = fileConfig.fileUrl || '';
    let mimetype = fileConfig.mimetype || '';
    let fileName = fileConfig.fileName || 'Unknown file';

    // Use queued file info when training is in progress
    if (
      trainingProgress === TrainingProgress.TRAINING_QUEUED ||
      trainingProgress === TrainingProgress.TRAINING_IN_PROGRESS
    ) {
      if (fileConfig.queuedFile) {
        fileUrl = fileConfig.queuedFile.fileUrl || fileConfig.fileUrl || '';
        mimetype = fileConfig.queuedFile.mimetype || fileConfig.mimetype || '';
        fileName = fileConfig.queuedFile.fileName || fileConfig.fileName || 'Unknown file';
      }
    }

    const displayType = this.getDisplayTypeFromMimetype(mimetype);
    const icon = this.fileTypeIconService.getIconForMimetype(mimetype);
    const downloadUrl = fileUrl ? this.fileDownloadService.getDownloadLink(fileUrl) : null;
    const viewUrl = fileUrl ? this.fileDownloadService.getViewLink(fileUrl) : null;

    return {
      fileName,
      mimetype,
      displayType,
      icon,
      fileUrl,
      downloadUrl,
      viewUrl,
    };
  });

  // Common type mapping for file types
  private readonly fileTypeMap: { [key: string]: string } = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/msword': 'DOC',
    'text/plain': 'TXT',
    'text/html': 'HTML',
    'text/csv': 'CSV',
    'application/json': 'JSON',
    'text/markdown': 'Markdown',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-excel': 'XLS',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/jpg': 'JPG',
  };

  // Helper method to get display type from mimetype
  private getDisplayTypeFromMimetype(mimetype: string): string {
    return this.fileTypeMap[mimetype] || mimetype.split('/')[1]?.toUpperCase() || 'Unknown';
  }

  // Errored file display information computed signal
  readonly erroredFileInfo = computed(() => {
    const fileConfig = this.knowledgeSource()?.config?.fileConfig;
    if (!fileConfig?.erroredFile || !this.hasTrainingError()) {
      return null;
    }

    const mimetype = fileConfig.erroredFile.mimetype || '';
    const fileName = fileConfig.erroredFile.fileName || 'Unknown file';
    const fileUrl = fileConfig.erroredFile.fileUrl;
    const displayType = this.getDisplayTypeFromMimetype(mimetype);
    const icon = this.fileTypeIconService.getIconForMimetype(mimetype);
    const downloadUrl = fileUrl ? this.fileDownloadService.getDownloadLink(fileUrl) : null;
    const viewUrl = fileUrl ? this.fileDownloadService.getViewLink(fileUrl) : null;

    return {
      fileName,
      mimetype,
      displayType,
      icon,
      fileUrl,
      downloadUrl,
      viewUrl,
    };
  });

  // Validation computed signals
  readonly hasValidFile = computed(() => {
    const state = this.fileState();
    const hasUploadedFile = state.file?.status === FileUploadStatus.Success;
    const noFileUploading = !state.isUploading;
    const hasJwtToken = !!state.jwtToken;
    const hasExistingFile = state.hasExistingFile;

    // Valid if there's either a new uploaded file OR an existing file
    return (hasUploadedFile && noFileUploading && hasJwtToken) || hasExistingFile;
  });

  // Signal to track form validity reactively
  private readonly formValiditySignal = signal(false);

  readonly isFormValidForCurrentStep = computed(() => {
    const currentStep = this.selectedStep();
    switch (currentStep) {
      case 0: {
        // Use the reactive form validity signal instead of direct property access
        const hasValidName = this.formValiditySignal();
        const hasValidFile = this.hasValidFile();
        return !!(hasValidName && hasValidFile);
      }
      case 1: {
        return true;
      }
      default:
        return false;
    }
  });

  readonly currentValidationErrors = computed(() => {
    const currentStep = this.selectedStep();
    const errors: string[] = [];

    if (currentStep === 0) {
      const hasValidName = this.formValiditySignal();
      const nameControl = this.form?.get('name');
      const state = this.fileState();

      // Check name validation - only show error if field is empty
      if (!hasValidName && !nameControl?.value?.trim()) {
        errors.push('EDIT_KNOWLEDGE.FIELD_REQUIRED');
      }

      // Check file validation
      const hasUploadedFile = state.file?.status === FileUploadStatus.Success;
      const hasExistingFile = state.hasExistingFile;

      if (!hasUploadedFile && !hasExistingFile) {
        errors.push('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.NO_FILE_UPLOADED');
      } else if (hasUploadedFile && state.isUploading) {
        errors.push('COMMON.UPLOADING');
      } else if (hasUploadedFile && !state.jwtToken) {
        errors.push('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.UPLOAD_PROCESSING');
      }
    }

    return errors;
  });

  readonly hasUnsavedChanges = computed(() => {
    const currentStep = this.selectedStep();
    switch (currentStep) {
      case 0: {
        const state = this.fileState();
        // If we've successfully saved, then there are no unsaved changes
        if (state.hasBeenSaved) {
          return false;
        }
        return this.form?.dirty || state.file !== null;
      }
      case 1: {
        return this.applicationSelector?.hasUnsavedChanges ?? false;
      }
      default:
        return false;
    }
  });

  // Stepper computed signals
  readonly isOnLastStep = computed(() => this.selectedStep() === this.steps().length - 1);
  readonly shouldShowBack = computed(() => this.selectedStep() > 0);
  readonly submitActionText = computed(() => {
    return this.isOnLastStep() ? 'COMMON.SAVE' : 'COMMON.NEXT';
  });

  constructor(
    readonly destroyRef: DestroyRef,
    public dialogRef: MatDialogRef<FileUploadComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Data,
    @Inject(GALAXY_UPLOADER_SERVICE_TOKEN) private uploaderService: GalaxyUploaderService,
    private fileDownloadService: FileDownloadService,
  ) {
    this.form = new FormGroup({
      name: new FormControl('', [Validators.required]),
    });
  }

  async ngOnInit(): Promise<void> {
    this.dialogRef.disableClose = true;

    // Initialize upload URL based on environment
    const environment = this.environmentService.getEnvironment();
    const baseUrl = this.uploadHosts.get(environment) || this.uploadHosts.get(Environment.DEMO);
    this.uploadUrl = `${baseUrl}/api/v1/upload-file`;

    if (this.data) {
      if (this.data?.knowledgeSourceKey) {
        await this.knowledgeService
          .getKnowledgeSource(this.data.knowledgeSourceKey.knowledgeSourceId, this.data.knowledgeSourceKey.namespace)
          .then(
            (knowledgeSource) => {
              this.knowledgeSource.set(knowledgeSource as KnowledgeSourceWithSuggestedFlag);
            },
            (_) => {
              this.knowledgeSource.set(null);
            },
          );

        this.otherAIDisclaimer.set(
          await this.knowledgeService.getOtherAIDisclaimerText(
            this.data.knowledgeSourceKey.knowledgeSourceId,
            this.data.currentAppId,
          ),
        );
      }

      this.excludeAddToAIStep = this.data.excludeAddToAIStep ?? false;
    }

    if (!this.excludeAddToAIStep) {
      this.steps.update((steps) => [...steps, 'appSelection']);
    }

    this.form = new FormGroup(
      {
        name: new FormControl(this.knowledgeSource()?.name || '', [Validators.required]),
      },
      { validators: [this.fileUploadValidator.bind(this)] },
    );

    // Initialize file state based on existing knowledge source
    if (this.knowledgeSource()) {
      const hasExistingFile = !!this.knowledgeSource()?.config?.fileConfig?.fileUrl;
      this.updateFileState({
        hasExistingFile,
        hasBeenSaved: true, // Existing knowledge source is considered "saved"
      });

      if (!isTrainingFinished(this.knowledgeSource()?.training)) {
        this.pollUntilTrainingFinishes(this.knowledgeSource()!.id);
      }
    }

    // Reset saved state when form changes and update form validity signal
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateFileState({ hasBeenSaved: false });
      this.updateFormValiditySignal();
    });

    // Also listen to status changes (validity changes)
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updateFormValiditySignal();
    });

    // Initial form validity update
    this.updateFormValiditySignal();

    this.isLoading.set(false);
  }

  onFileUploaded(fileInfo: FileInfo): void {
    this.handleSuccessfulUpload(fileInfo);
    this.autoFillNameFromFile(fileInfo.name);
    this.snackbarService.openSuccessSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.UPLOAD_SUCCESS');
    this.updateFormValidation();
  }

  onFileUploadError(error: FileUploadError): void {
    if (this.isReplaceableError(error)) {
      this.handleFileReplacement(error.fileInfo);
      return;
    }

    this.snackbarService.openErrorSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.UPLOAD_ERROR');
    this.updateFileState({ jwtToken: null });
  }

  onFilesChanged(files: FileInfo[]): void {
    this.updateFileFromChanges(files);
    this.updateFormValidation();
  }

  // Helper methods for file upload operations
  private handleSuccessfulUpload(fileInfo: FileInfo): void {
    const jwtToken = fileInfo.resp?.data?.url || null;
    this.updateFileState({
      file: fileInfo,
      jwtToken,
      hasBeenSaved: false,
    });
  }

  private autoFillNameFromFile(fileName: string | undefined): void {
    if (fileName && !this.form.get('name')?.value) {
      const baseName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension
      this.form.get('name')?.setValue(baseName);
    }
  }

  private isReplaceableError(error: FileUploadError): boolean {
    return (
      error.error?.message === 'GALAXY.UPLOADER.ERROR.DUPLICATE_FILE_ATTEMPT' ||
      error.error?.message === 'GALAXY.UPLOADER.ERROR.MAX_FILES_EXCEEDED'
    );
  }

  private handleFileReplacement(fileInfo: FileInfo): void {
    this.uploaderService.clear();
    this.resetFileState();
    this.snackbarService.openSuccessSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.FILE_REPLACED');
    this.updateFormValidation();

    // Re-add the file after clearing
    setTimeout(() => {
      if (fileInfo.file && fileInfo.context) {
        this.uploaderService.addFile(fileInfo.file, fileInfo.context);
      }
    }, 100);
  }

  private updateFileFromChanges(files: FileInfo[]): void {
    const file = files.length > 0 ? files[0] : null;
    const isUploading = file?.status === FileUploadStatus.InProgress;

    this.updateFileState({
      file,
      isUploading,
      jwtToken: !file || file.status !== FileUploadStatus.Success ? null : this.fileState().jwtToken,
      hasBeenSaved: false,
    });
  }

  private resetFileState(): void {
    this.updateFileState({
      file: null,
      isUploading: false,
      jwtToken: null,
      hasBeenSaved: false,
    });
  }

  private updateFormValidation(): void {
    this.form.updateValueAndValidity();
    this.updateFormValiditySignal();
  }

  private updateFormValiditySignal(): void {
    const nameControl = this.form?.get('name');
    const isNameValid = nameControl?.valid ?? false;
    this.formValiditySignal.set(isNameValid);
  }

  private updateFileState(updates: Partial<FileUploadState>): void {
    this.fileState.update((current) => ({ ...current, ...updates }));
  }

  private fileUploadValidator(): { [key: string]: any } | null {
    return this.hasValidFile() ? null : { fileRequired: true };
  }

  async submit(): Promise<void> {
    if (!this.hasUnsavedChanges()) {
      if (!this.isOnLastStep()) this.stepper.next();
      else this.close();

      return;
    }

    if (!this.isFormValid()) {
      this.snackbarService.openErrorSnack('COMMON.INVALID_FORM');
      return;
    }

    const currentStep = this.selectedStep();
    switch (currentStep) {
      case 0:
        await this.handleStepZeroSubmit();
        break;
      case 1:
        await this.handleStepOneSubmit();
        break;
    }
  }

  private async handleStepZeroSubmit(): Promise<void> {
    const name = this.form.get('name')?.value;
    const state = this.fileState();
    const hasNewFile = !!state.jwtToken;
    const hasExistingFile = state.hasExistingFile;

    // Validate file requirements
    if (!hasNewFile && !hasExistingFile) {
      this.snackbarService.openErrorSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.NO_FILE_UPLOADED');
      return;
    }

    const ksId = hasNewFile ? await this.handleNewFileUpload(name, state) : await this.handleNameOnlyUpdate(name);

    if (ksId) {
      await this.handleSuccessfulSave(ksId);

      // Refresh knowledge source from backend to get current training state
      if (hasNewFile && this.knowledgeSource()?.id) {
        this.knowledgeSource.set(
          (await this.knowledgeService.getKnowledgeSource(
            this.knowledgeSource()!.id,
            this.knowledgeService.buildNamespaceInterface(),
          )) as KnowledgeSourceWithSuggestedFlag,
        );
      }
    }
  }

  private async handleStepOneSubmit(): Promise<void> {
    await this.applicationSelector.setAppsForKnowledgeSource();
    // Close directly without warning check since we just saved
    this.stepChange.emit('');
    this.dialogRef.close();
  }

  private async handleNewFileUpload(name: string, state: FileUploadState): Promise<string | null> {
    const queuedFile = this.createQueuedFile(state);

    if (this.knowledgeSource()?.id) {
      const currentKs = this.knowledgeSource()!;
      currentKs.name = name;
      currentKs.config.fileConfig = new KnowledgeSourceConfigFileConfig({
        fileUrl: currentKs.config.fileConfig.fileUrl,
        mimetype: currentKs.config.fileConfig.mimetype,
        fileName: currentKs.config.fileConfig.fileName,
        queuedFile: queuedFile,
      });
      this.knowledgeSource.set(currentKs);
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(currentKs);
      return currentKs.id;
    } else {
      // Create new knowledge source
      const filename = state.file?.name || 'Unknown file';
      const description = `Created from ${filename}`;
      const config = new KnowledgeSourceConfig({
        fileConfig: {
          queuedFile: queuedFile,
        },
      });
      return await this.knowledgeService.createKnowledgeSourceWithDescription(name, description, config);
    }
  }

  private async handleNameOnlyUpdate(name: string): Promise<string | null> {
    if (this.knowledgeSource()?.id) {
      const currentKs = this.knowledgeSource()!;
      currentKs.name = name;
      this.knowledgeSource.set(currentKs);
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(currentKs);
      return currentKs.id;
    } else {
      // This shouldn't happen - we can't have an existing file without an existing knowledge source
      this.snackbarService.openErrorSnack('EDIT_KNOWLEDGE.FILE_UPLOAD_CONFIG.NO_FILE_UPLOADED');
      return null;
    }
  }

  private createQueuedFile(state: FileUploadState) {
    let mimetype = state.file?.file?.type;
    if (!mimetype) {
      const fileExtension = state.file?.file?.name?.split('.')?.pop()?.toLowerCase();
      mimetype = (fileExtension && mime.getType(fileExtension)) || 'application/octet-stream';
    }
    const filename = state.file?.name || 'Unknown file';

    return new KnowledgeSourceConfigFileConfigFile({
      fileUrl: state.jwtToken ?? undefined,
      mimetype: mimetype,
      fileName: filename,
    });
  }

  private async handleSuccessfulSave(ksId: string): Promise<void> {
    this.knowledgeCreated.emit(ksId);
    this.form.markAsPristine();
    this.updateFileState({ hasBeenSaved: true });
    this.pollUntilTrainingFinishes(ksId);
  }

  private isFormValid(): boolean {
    // Use computed signal for pure validation
    if (this.isFormValidForCurrentStep()) {
      return true;
    }

    // Handle side effects for validation errors
    this.handleValidationSideEffects();
    return false;
  }

  private handleValidationSideEffects(): void {
    const currentStep = this.selectedStep();

    if (currentStep === 0) {
      this.markStepOneFieldsAsDirty();
      this.showValidationErrors();
    }
  }

  private markStepOneFieldsAsDirty(): void {
    const nameControl = this.form?.get('name');
    nameControl?.markAsDirty();
  }

  private showValidationErrors(): void {
    const errors = this.currentValidationErrors();

    // Show the first relevant error
    if (errors.length > 0) {
      // Prioritize name errors first, then file errors
      const hasValidName = this.formValiditySignal();
      if (!hasValidName) {
        // Name validation error - let form field handle display
        return;
      }

      // Show file-related errors via snackbar
      const fileErrors = errors.filter(
        (error) => error.includes('FILE_UPLOAD') || error.includes('UPLOADING') || error.includes('UPLOAD_PROCESSING'),
      );

      if (fileErrors.length > 0) {
        this.snackbarService.openErrorSnack(fileErrors[0]);
      }
    }
  }

  stepChanged(event: StepperSelectionEvent): void {
    this.selectedStep.set(event.selectedIndex);
    this.stepChange.emit(this.steps()[this.selectedStep()]);
  }

  back(): void {
    if (this.shouldShowBack()) {
      this.stepper.previous();
    }
  }

  readonly isStepZeroComplete = computed(() => {
    // Check if step 0 has valid name and file (regardless of current step)
    const hasValidName = this.formValiditySignal();
    const hasValidFile = this.hasValidFile();

    return !!(hasValidName && hasValidFile);
  });

  async createWebChatWidget(): Promise<void> {
    await this.webChatWidgetService.createWebChatWidget({
      dialogRef: this.dialogRef,
      knowledgeSource: this.knowledgeSource(),
      applicationSelector: this.applicationSelector,
    });
  }

  warnUnsavedChanged(func: () => void): void {
    if (!this.hasUnsavedChanges()) {
      func();
      return;
    }

    this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('UNSAVED_CHANGES.TITLE'),
        message: this.translateService.instant('UNSAVED_CHANGES.MESSAGE'),
        confirmButtonText: this.translateService.instant('COMMON.YES'),
        cancelButtonText: this.translateService.instant('COMMON.NO'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          func();
        }
      });
  }

  close(): void {
    this.warnUnsavedChanged(() => {
      this.stepChange.emit('');
      this.dialogRef.close();
    });
  }

  getCurrentFileName(): string {
    return this.currentFileInfo()?.fileName || 'Unknown file';
  }

  getCurrentFileType(): string {
    return this.currentFileInfo()?.displayType || '';
  }

  getFileIcon(): string {
    return this.currentFileInfo()?.icon || 'description';
  }

  getCurrentFileDownloadUrl(): string | null {
    return this.currentFileInfo()?.downloadUrl || null;
  }

  getCurrentFileViewUrl(): string | null {
    return this.currentFileInfo()?.viewUrl || null;
  }

  viewCurrentFile(): void {
    const fileInfo = this.currentFileInfo();
    if (fileInfo?.viewUrl) {
      window.open(fileInfo.viewUrl, '_blank');
    }
  }

  downloadCurrentFile(): void {
    const fileInfo = this.currentFileInfo();
    if (fileInfo?.downloadUrl) {
      const link = document.createElement('a');
      link.href = fileInfo.downloadUrl;
      link.download = fileInfo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Errored file helper methods
  getErroredFileName(): string {
    return this.erroredFileInfo()?.fileName || 'Unknown file';
  }

  getErroredFileType(): string {
    return this.erroredFileInfo()?.displayType || '';
  }

  getErroredFileIcon(): string {
    return this.erroredFileInfo()?.icon || 'description';
  }

  getErroredFileDownloadUrl(): string | null {
    return this.erroredFileInfo()?.downloadUrl || null;
  }

  getErroredFileViewUrl(): string | null {
    return this.erroredFileInfo()?.viewUrl || null;
  }

  viewErroredFile(): void {
    const fileInfo = this.erroredFileInfo();
    if (fileInfo?.viewUrl) {
      window.open(fileInfo.viewUrl, '_blank');
    }
  }

  downloadErroredFile(): void {
    const fileInfo = this.erroredFileInfo();
    if (fileInfo?.downloadUrl) {
      const link = document.createElement('a');
      link.href = fileInfo.downloadUrl;
      link.download = fileInfo.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  pollUntilTrainingFinishes(ksId: string): void {
    this.submitDisabled.set(true);
    if (this.isOnLastStep()) {
      this.shouldShowDone.set(true);
    }
    defer(() => from(this.knowledgeService.getKnowledgeSource(ksId, this.knowledgeService.buildNamespaceInterface())))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        map((ks) => {
          this.knowledgeSource.set(ks as KnowledgeSourceWithSuggestedFlag);
          if (!isTrainingFinished(this.knowledgeSource()?.training)) {
            this.submitDisabled.set(true);
            this.delayProgressScreenClosure.set(true);
            // throw error to poll again
            throw false;
          }

          // Do not show "Training Complete" when there's an error
          if (this.knowledgeSource()?.training?.progress === TrainingProgress.TRAINING_ERRORED) {
            this.shouldShowDone.set(false);
          }

          setTimeout(() => {
            // Only proceed to next step if training was successful
            if (this.knowledgeSource()?.training?.progress === TrainingProgress.TRAINING_DONE && !this.isOnLastStep()) {
              this.stepper.next();
            }

            // This line fixes the console error message: "ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked."
            this.cdr.detectChanges();
            this.delayProgressScreenClosure.set(false);
            this.submitDisabled.set(false);
          }, 1000);
        }),
        retry({ delay: 5000 }),
      )
      .subscribe();
  }

  protected readonly TrainingProgress = TrainingProgress;

  async dismissTrainingError(): Promise<void> {
    if (!this.knowledgeSource()?.id) {
      return;
    }

    this.isDismissingError.set(true);
    this.submitDisabled.set(true);

    try {
      await this.knowledgeService.dismissKnowledgeSourceTrainingError(this.knowledgeSource()!.id);

      // Refresh the knowledge source to get the updated state
      this.knowledgeSource.set(
        (await this.knowledgeService.getKnowledgeSource(
          this.knowledgeSource()!.id,
          this.knowledgeService.buildNamespaceInterface(),
        )) as KnowledgeSourceWithSuggestedFlag,
      );
    } finally {
      this.isDismissingError.set(false);
      this.submitDisabled.set(false);
    }
  }

  async clearErroredFile(): Promise<void> {
    // If we can dismiss the error, do so first
    if (this.canDismissError()) {
      await this.dismissTrainingError();
    }

    // Reset the file state to allow uploading a new file
    this.updateFileState({
      file: null,
      jwtToken: null,
      isUploading: false,
      hasBeenSaved: false,
      hasExistingFile: false,
    });
  }

  private clearTrainingErrorState(): void {
    // Clear the error state locally - this will hide the error UI
    if (this.knowledgeSource()?.training) {
      const currentKs = this.knowledgeSource()!;
      currentKs.training = new KnowledgeSourceTraining({
        progress: TrainingProgress.TRAINING_QUEUED,
        totalUnits: currentKs.training.totalUnits,
        completedUnits: currentKs.training.completedUnits,
      });
      this.knowledgeSource.set(currentKs);
    }
  }
}
